import React from 'react';

export default function Roadmap() {
  const roadmapItems = [
    {
      quarter: 'Q2 2025',
      status: 'completed',
      title: 'Platform Foundation',
      items: [
        'Establish Diagnostic Intelligence platform architecture',
        'Develop disease-oriented multimodal intelligence framework',
        'Build core data, model, and provenance infrastructure',
        'Establish initial pulmonary disease focus with TB as the starting use case',
      ],
    },
    {
      quarter: 'Q3 2025',
      status: 'completed',
      title: 'Multimodal Core',
      items: [
        'Integrate radiology, pathology, clinical, and acoustic modalities',
        'Develop modality-specific representation pipelines',
        'Establish shared disease-oriented semantic representation',
        'Develop multimodal biomarker intelligence workflows',
      ],
    },
    {
      quarter: 'Q4 2025',
      status: 'completed',
      title: 'Disease Intelligence',
      items: [
        'Develop disease characterization and patient stratification capabilities',
        'Integrate ontology-anchored clinical and disease concepts',
        'Develop disease semantic and prototype analysis workflows',
        'Establish explainability, provenance, and evidence tracking',
      ],
    },
    {
      quarter: 'Q1 2026',
      status: 'completed',
      title: 'Validation & Refinement',
      items: [
        'Strengthen multimodal model evaluation and benchmarking',
        'Evaluate multimodal approaches against unimodal and ensemble baselines',
        'Refine model reliability, uncertainty, and missing-modality handling',
        'Advance translational research workflows for pulmonary disease',
      ],
    },
    {
      quarter: 'Q2 2026',
      status: 'completed',
      title: 'Pilot Readiness',
      items: [
        'Prepare platform workflows for research and clinical pilot collaborations',
        'Strengthen data ingestion and multimodal processing pipelines',
        'Develop stakeholder-oriented clinical intelligence workflows',
        'Advance deployment, governance, and integration readiness',
      ],
    },
    {
      quarter: 'Q3 2026',
      status: 'in-progress',
      title: 'Clinical & Research Expansion',
      items: [
        'Expand disease intelligence workflows beyond the initial TB focus',
        'Advance multimodal biomarker discovery and disease characterization',
        'Extend research capabilities across additional pulmonary conditions',
        'Support collaboration with healthcare, research, and industry partners',
      ],
    },
    {
      quarter: 'Q4 2026',
      status: 'planned',
      title: 'Precision Medicine & Biomarker Intelligence',
      items: [
        'Advance treatment-response and precision-medicine research workflows',
        'Expand biomarker discovery and disease mechanism investigation',
        'Extend integration toward molecular and multi-omics data',
        'Strengthen longitudinal disease and treatment analysis capabilities',
      ],
    },
    {
      quarter: 'Q1 2027',
      status: 'planned',
      title: 'Scale & Ecosystem',
      items: [
        'Expand multimodal intelligence across additional disease areas',
        'Broaden research and pilot partnerships',
        'Extend platform integration and deployment capabilities',
        'Establish a scalable foundation for healthcare, life sciences, and MedTech applications',
      ],
    },
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'bg-teal/20 text-teal border-teal/30';
      case 'in-progress':
        return 'bg-amber-100 text-amber-700 border-amber-300';
      case 'planned':
        return 'bg-slate-100 text-slate-700 border-slate-300';
      default:
        return 'bg-slate-100 text-slate-700 border-slate-300';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'completed':
        return '✓ Completed';
      case 'in-progress':
        return '↻ In Progress';
      case 'planned':
        return '○ Planned';
      default:
        return status;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-tealLight/30 to-paper text-ink antialiased selection:bg-teal/20 selection:text-ink">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12 font-sans">

        {/* Page Header */}
        <div className="text-center mb-16">
          <div className="inline-block mb-4">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-tealLight border border-teal/20 text-teal text-xs font-semibold uppercase tracking-wider">
              Updated Product Roadmap
            </div>
          </div>

          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-sans font-bold leading-tight tracking-tight text-center text-teal">
            Updated Product Roadmap
          </h1>

          <p className="mt-6 text-lg sm:text-xl text-inkSoft max-w-3xl mx-auto leading-relaxed text-center">
            The evolution of Diagnostic Intelligence from its initial
            multimodal foundation toward scalable disease intelligence,
            precision medicine, and translational research.
          </p>
        </div>

        {/* Roadmap */}
        <div className="relative">
          <div className="absolute left-8 top-0 bottom-0 w-1 bg-teal/30"></div>

          {roadmapItems.map((item, index) => (
            <div key={index} className="mb-12 pl-16 relative">

              {/* Timeline Number */}
              <div className="absolute left-4 top-1 w-8 h-8 bg-teal rounded-full border-4 border-white flex items-center justify-center shadow-md">
                <span className="text-white text-xs font-bold">
                  {index + 1}
                </span>
              </div>

              {/* Roadmap Card */}
              <div className="group relative bg-white border border-rule rounded-xl p-6 shadow-[4px_4px_0_0_rgba(20,184,166,0.18)] hover:shadow-[6px_6px_0_0_rgba(20,184,166,0.3)] hover:-translate-y-0.5 hover:-translate-x-0.5 transition-all duration-200">

                <div className="flex flex-wrap items-start justify-between gap-4 mb-3">

                  <div>
                    <div className="text-sm font-semibold text-teal uppercase tracking-wide mb-1">
                      {item.quarter}
                    </div>

                    <h3 className="text-xl font-bold text-ink group-hover:text-teal transition-colors">
                      {item.title}
                    </h3>
                  </div>

                  <span
                    className={`px-3 py-1 rounded-lg text-xs font-semibold border whitespace-nowrap ${getStatusColor(
                      item.status
                    )}`}
                  >
                    {getStatusLabel(item.status)}
                  </span>
                </div>

                <ul className="space-y-2 mt-5">
                  {item.items.map((task, idx) => (
                    <li
                      key={idx}
                      className="flex items-start gap-3 text-sm text-inkSoft"
                    >
                      <span className="text-teal mt-0.5 flex-shrink-0">
                        ▹
                      </span>
                      <span>{task}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>

        {/* Roadmap Note */}
        <div className="mt-16 p-6 bg-white border border-rule rounded-xl shadow-[4px_4px_0_0_rgba(20,184,166,0.18)] text-center">
          <p className="text-inkSoft text-sm leading-relaxed">
            Our roadmap is dynamic and evolves with research findings,
            validation results, clinical and industry feedback, partnership
            opportunities, and technology development. Future capabilities
            are subject to research validation and product priorities.
          </p>
        </div>

      </div>
    </div>
  );
}
